import { ChatbotEntry, ChatbotLink } from './chatbotService';

export interface BotReply {
  text: string;
  entry?: ChatbotEntry;
  related?: ChatbotEntry[];
  links?: ChatbotLink[];
}

// 답변을 찾지 못했을 때 안내하는 연락처는 관리자 데이터와 상관없이 항상 보여준다
export const FALLBACK_REPLY: BotReply = {
  text: '죄송해요, 질문을 정확히 이해하지 못했어요. 아래 메뉴를 선택하시거나 담당자에게 문의해 주세요.',
  links: [
    { label: '견적문의 작성하기', url: '/quote' },
    { label: '자주묻는질문 전체보기', url: '/faq' },
    { label: '전화 053-216-4126', url: 'tel:0532164126' },
  ],
};

const normalize = (value: string) => value.toLowerCase().replace(/[\s?!.,~·]/g, '');

export const splitKeywords = (keywords: string) =>
  Array.from(new Set(keywords.split(/[\s,]+/).map(normalize).filter((word) => word.length >= 2)));

// 방문자 질문에 포함된 키워드로 점수를 매긴다.
// 긴 키워드("시안확인")일수록, 여러 답변에 흔하게 쓰인 키워드("견적")일수록 덜 중요하게 본다.
// 점수가 같으면 관리자가 정한 순서(entries 순서)가 앞선 답변이 먼저 나온다.
export function rankEntries(query: string, entries: ChatbotEntry[], limit = 3): ChatbotEntry[] {
  const text = normalize(query);
  if (!text) return [];

  const perEntry = entries.map((entry) => ({ entry, keywords: splitKeywords(entry.keywords) }));
  const docFreq = new Map<string, number>();
  perEntry.forEach(({ keywords }) => keywords.forEach((word) => docFreq.set(word, (docFreq.get(word) ?? 0) + 1)));

  return perEntry
    .map(({ entry, keywords }) => ({
      entry,
      score: keywords.reduce(
        (sum, word) => (text.includes(word) ? sum + word.length / (docFreq.get(word) ?? 1) : sum),
        0,
      ),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ entry }) => entry);
}

export function replyFor(entry: ChatbotEntry, related?: ChatbotEntry[]): BotReply {
  return { text: entry.answer, entry, links: entry.links, related: related?.length ? related : undefined };
}

export function answer(query: string, entries: ChatbotEntry[]): BotReply {
  const [best, ...related] = rankEntries(query, entries);
  return best ? replyFor(best, related) : FALLBACK_REPLY;
}
